import { createFocusTrap } from '../../../utils/createFocusTrap';
import { RefObject, useEffect, useRef } from 'react';

interface FocusTrapOptions {
  autoFocus?: boolean;
  restoreFocus?: boolean;
}

export const useFocusTrap = (
  isActive: boolean,
  containerRef: RefObject<HTMLElement>,
  options: FocusTrapOptions = {}
): void => {
  const focusTrapRef = useRef<ReturnType<typeof createFocusTrap>>();

  useEffect(() => {
    // Always clean up existing trap first
    if (focusTrapRef.current) {
      focusTrapRef.current.deactivate();
      focusTrapRef.current = undefined;
    }

    // Early return if not active
    if (!isActive) {
      return;
    }

    // Create new trap instance with current options
    focusTrapRef.current = createFocusTrap({
      autoFocus: options.autoFocus,
      restoreFocus: options.restoreFocus,
    });

    // Activate when container is available
    const activate = () => {
      if (focusTrapRef.current && containerRef.current) {
        // Use requestAnimationFrame to ensure DOM is painted
        requestAnimationFrame(() => {
          if (focusTrapRef.current && containerRef.current) {
            focusTrapRef.current.activate(containerRef.current);
          }
        });
      }
    };

    // Immediate activation if container is ready, otherwise wait
    if (containerRef.current) {
      activate();
    } else {
      const timeoutId = setTimeout(activate, 0);
      return () => {
        clearTimeout(timeoutId);
        if (focusTrapRef.current) {
          focusTrapRef.current.deactivate();
        }
      };
    }

    // Cleanup function - FIXED: Always deactivate, not just on timeout
    return () => {
      if (focusTrapRef.current) {
        focusTrapRef.current.deactivate();
      }
    };
  }, [isActive, containerRef, options.autoFocus, options.restoreFocus]); // FIXED: Added containerRef dependency

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (focusTrapRef.current) {
        focusTrapRef.current.deactivate();
      }
    };
  }, []);
};
